/**
 * CSS Chunk 错误处理工具
 * 用于处理UmiJS代码分割时CSS chunk加载失败的情况
 */

interface CSSRetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  exponentialBackoff?: boolean;
}

class CSSChunkErrorHandler {
  private static instance: CSSChunkErrorHandler;
  private retryCounts: Map<string, number> = new Map();
  private options: Required<CSSRetryOptions>;

  private constructor(options: CSSRetryOptions = {}) {
    this.options = {
      maxRetries: options.maxRetries || 3,
      retryDelay: options.retryDelay || 1000,
      exponentialBackoff: options.exponentialBackoff !== false,
    };
    this.init();
  }

  public static getInstance(options?: CSSRetryOptions): CSSChunkErrorHandler {
    if (!CSSChunkErrorHandler.instance) {
      CSSChunkErrorHandler.instance = new CSSChunkErrorHandler(options);
    }
    return CSSChunkErrorHandler.instance;
  }

  /**
   * 初始化错误处理器
   */
  private init(): void {
    // 监听CSS加载错误
    document.addEventListener('error', this.handleCSSLoadError.bind(this), true);
    
    // 监听未处理的Promise拒绝
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    
    // 监听页面可见性变化，当页面重新可见时尝试重新加载失败的CSS
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  /**
   * 处理CSS加载错误
   */
  private handleCSSLoadError(event: Event): void {
    const target = event.target as HTMLElement;
    
    if (target && target.tagName === 'LINK' && target.getAttribute('rel') === 'stylesheet') {
      const linkElement = target as HTMLLinkElement;
      const href = linkElement.href;
      
      if (this.isChunkCSS(href)) {
        console.warn('检测到CSS chunk加载失败:', href);
        this.retryLoadCSS(linkElement);
      }
    }
  }

  /**
   * 处理未处理的Promise拒绝
   */
  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const reason = event.reason;
    
    if (typeof reason === 'string' && this.isChunkError(reason)) {
      console.warn('检测到CSS chunk加载Promise拒绝:', reason);
      
      // 阻止默认的错误处理
      event.preventDefault();
      
      // 尝试刷新页面
      this.suggestPageReload();
    }
  }

  /**
   * 处理页面可见性变化
   */
  private handleVisibilityChange(): void {
    if (!document.hidden) {
      // 页面重新可见时，尝试重新加载之前失败的CSS
      this.retryFailedCSS();
    }
  }

  /**
   * 判断是否是chunk CSS文件
   */
  private isChunkCSS(href: string): boolean {
    return href && (
      href.includes('vendors-async.css') ||
      href.includes('chunk') ||
      href.includes('async') ||
      href.includes('vendors')
    );
  }

  /**
   * 判断是否是chunk相关错误
   */
  private isChunkError(message: string): boolean {
    return message.includes('Loading CSS chunk') ||
           message.includes('vendors-async.css') ||
           message.includes('ChunkLoadError');
  }

  /**
   * 重试加载CSS文件
   */
  private retryLoadCSS(linkElement: HTMLLinkElement): void {
    const href = linkElement.href;
    const currentRetries = this.retryCounts.get(href) || 0;
    
    if (currentRetries >= this.options.maxRetries) {
      console.error(`CSS文件重试加载失败，已达到最大重试次数 (${this.options.maxRetries}):`, href);
      this.retryCounts.delete(href);
      return;
    }

    // 更新重试次数
    this.retryCounts.set(href, currentRetries + 1);
    
    // 计算延迟时间
    const delay = this.options.exponentialBackoff 
      ? this.options.retryDelay * Math.pow(2, currentRetries)
      : this.options.retryDelay;
    
    console.log(`CSS文件重试加载 (${currentRetries + 1}/${this.options.maxRetries}):`, href);
    
    setTimeout(() => {
      this.createAndLoadCSS(href, linkElement);
    }, delay);
  }

  /**
   * 创建并加载CSS文件
   */
  private createAndLoadCSS(href: string, originalLink: HTMLLinkElement): void {
    const newLink = document.createElement('link');
    newLink.rel = 'stylesheet';
    newLink.href = href;
    newLink.type = 'text/css';
    
    // 添加加载成功和失败的处理
    newLink.onload = () => {
      console.log('CSS文件重新加载成功:', href);
      this.retryCounts.delete(href);
      
      // 移除旧的失败元素
      if (originalLink.parentNode) {
        originalLink.parentNode.removeChild(originalLink);
      }
    };
    
    newLink.onerror = () => {
      console.warn('CSS文件重试加载失败:', href);
      // 继续重试
      this.retryLoadCSS(originalLink);
    };
    
    // 插入新的link元素
    if (originalLink.parentNode) {
      originalLink.parentNode.insertBefore(newLink, originalLink);
    }
  }

  /**
   * 重试之前失败的CSS
   */
  private retryFailedCSS(): void {
    const failedCSS = Array.from(this.retryCounts.keys());
    if (failedCSS.length > 0) {
      console.log('页面重新可见，尝试重新加载失败的CSS:', failedCSS);
      // 清除重试计数，允许重新尝试
      this.retryCounts.clear();
    }
  }

  /**
   * 建议刷新页面
   */
  private suggestPageReload(): void {
    // 延迟显示提示，避免在页面加载过程中弹出
    setTimeout(() => {
      if (confirm('检测到资源加载失败，是否刷新页面？')) {
        window.location.reload();
      }
    }, 500);
  }

  /**
   * 手动重试加载指定的CSS文件
   */
  public retryCSSFile(href: string): void {
    const linkElements = document.querySelectorAll(`link[href="${href}"]`);
    linkElements.forEach((link) => {
      if (link.getAttribute('rel') === 'stylesheet') {
        this.retryLoadCSS(link as HTMLLinkElement);
      }
    });
  }

  /**
   * 清除重试计数
   */
  public clearRetryCounts(): void {
    this.retryCounts.clear();
  }

  /**
   * 获取当前失败的文件信息
   */
  public getFailedFiles(): Array<{ href: string; retryCount: number }> {
    return Array.from(this.retryCounts.entries()).map(([href, count]) => ({
      href,
      retryCount: count,
    }));
  }
}

// 导出单例实例
export const cssChunkErrorHandler = CSSChunkErrorHandler.getInstance();

// 导出类，允许自定义配置
export { CSSChunkErrorHandler };
